/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import './silo-settings.scss';

/* eslint-disable import/no-unresolved, import/default */

import siloSettingsTemplate from './silo-settings.tpl.html';

/* eslint-enable import/no-unresolved, import/default */

export default angular.module('agrolog.client.siloSettings', [])
    .directive('agSiloSettings', SiloSettings)
    .name;

/*@ngInject*/
function SiloSettings() {
    return {
        restrict: "E",
        scope: true,
        bindToController: {
            ctx: '='
        },
        controller: SiloSettingsController,
        controllerAs: 'vm',
        templateUrl: siloSettingsTemplate
    };
}

/* eslint-disable angular/angularelement */

/*@ngInject*/
function SiloSettingsController($scope, $rootScope, $log, $filter, $translate, $q, types, agTypes, assetService, attributeService, siloService, agUtils, userService) {

    let vm = this;

    vm.agTypes = agTypes;
    vm.siloTypes = agTypes.siloType;
    vm.show3DSettings = show3DSettings;
    vm.loading = false;

    vm.showData = userService.getAuthority() === 'TENANT_ADMIN' || userService.getAuthority() === 'CUSTOMER_USER';
    vm.siloId = null;
    vm.silo = null;
    vm.levelIndicator = null;
    vm.headSpaceSensor = null;
    vm.allowEditSettings = true;

    vm.canEditDisabledSensors = false;

    $scope.$watch('vm.silo.autoDisableSensors + vm.allowEditSettings', function() {
        var autoDisabled = vm.silo && vm.silo.autoDisableSensors;
        vm.canEditDisabledSensors = vm.allowEditSettings && !autoDisabled;
    })

    vm.maxHeadSpaceCo2WarningThresholdLabel = $translate.instant('ag.silo.max-head-space-co2-warning-threshold') + ", ppm";
    vm.maxHeadSpaceCo2CriticalThresholdLabel = $translate.instant('ag.silo.max-head-space-co2-critical-threshold') + ", ppm";

    var user = userService.getCurrentUser();

    if (user.authority === 'CUSTOMER_USER') {
        var userAdditionalInfo = userService.getCurrentUserDetails().additionalInfo;
        if (userAdditionalInfo.readOnly) {
            vm.allowEditSettings = false;
        }
    }

    vm.helpPageAction = agUtils.createHelpActionConfig("agSilo", "ag.docsPageSections.silo-settings");

    vm.updateEnabledSensors = updateEnabledSensors;
    vm.changeCropType = changeCropType;

    $scope.$watch('vm.ctx', function() {
        if (vm.ctx) {
            vm.ctx.widgetActions = [vm.helpPageAction];
            vm.ctx.hideTitlePanel = false;

            vm.siloId = null;
            if (vm.ctx.stateController) {
                vm.siloId = vm.ctx.stateController.getStateParams().entityId;
            }
            loadSilo();
            $scope.$watch(function() {
                    return vm.ctx.stateController.getStateParams().entityId;
                },
                function (newSiloId, prevSiloId) {
                    if (newSiloId && !angular.equals(newSiloId, prevSiloId)) {
                        vm.siloId = newSiloId;
                        vm.levelIndicator = null;
                        vm.headSpaceSensor = null;
                        loadSilo();
                    }
                }
            );
        }
    });

    $scope.$watch(function() { return vm.silo ? (vm.silo.label || vm.silo.name) : null; }, function(title) {
        if (vm.showData && vm.ctx && title) {
            vm.ctx.widgetTitle = $translate.instant('ag.silo.silo-settings') + ': ' + title;
        }
    });

    $scope.$on('$destroy', function() {
        reset();
    });

    $scope.$watch('vm.silo.label', () => {
        if (vm.silo && !$scope.theForm.$invalid) {
            assetService.saveAsset(vm.silo).then(function success() {
                $rootScope.$broadcast('siteAssetUpdated', vm.silo);
            });
        }
    });

    $scope.$watch('vm.silo.cropType + vm.silo.maxCropLevel + vm.silo.emptyCropLevel + vm.silo.fullCropLevel + vm.silo.customCropDensity + vm.silo.customCropGlidingAngle + vm.silo.maxTemperature + vm.silo.maxDeltaTemperature + vm.silo.maxMoisture + vm.silo.maxDeltaMoisture + vm.silo.maxCO2 + vm.silo.maxDeltaCO2 + ' +
        'vm.silo.maxHeadSpaceTemperatureThreshold + vm.silo.maxHeadSpaceHumidityThreshold + vm.silo.maxHeadSpaceCo2WarningThreshold + vm.silo.maxHeadSpaceCo2CriticalThreshold + vm.silo.minHeadSpaceDewpointTemperatureDiffThreshold + ' +
        'vm.silo.maxCropLevelThreshold + vm.silo.minCropLevelThreshold + vm.silo.disableAlarms + vm.silo.coordinate3dX + vm.silo.coordinate3dY + vm.silo.coordinate3dZ + vm.silo.viewrotation + vm.silo.autoDisableSensors', () => {
        if (vm.silo && !$scope.theForm.$invalid) {
            var attributes = [];
            var cropType = vm.silo.cropType;
            if (cropType !== null) {
                cropType = cropType.nameId;
            }
            attributes.push({key: 'cropType', value: cropType});
            attributes.push({key: 'maxCropLevel', value: vm.silo.maxCropLevel});
            attributes.push({key: 'emptyCropLevel', value: vm.silo.emptyCropLevel});
            attributes.push({key: 'fullCropLevel', value: vm.silo.fullCropLevel});
            // NOTE: customDropDensity and customCropGlidingAngle must have a value even if not set, otherwise it might make problems for weight-calculation
            attributes.push({key: 'customCropDensity', value: vm.silo.customCropDensity || 0});
            attributes.push({key: 'customCropGlidingAngle', value: vm.silo.customCropGlidingAngle || 0});
            attributes.push({key: 'maxTemperature', value: vm.silo.maxTemperature});
            attributes.push({key: 'maxDeltaTemperature', value: vm.silo.maxDeltaTemperature});
            attributes.push({key: 'maxMoisture', value: vm.silo.maxMoisture});
            attributes.push({key: 'maxDeltaMoisture', value: vm.silo.maxDeltaMoisture});
            attributes.push({key: 'maxCO2', value: vm.silo.maxCO2});
            attributes.push({key: 'maxDeltaCO2', value: vm.silo.maxDeltaCO2});
            attributes.push({key: 'maxCropLevelThreshold', value: vm.silo.maxCropLevelThreshold});
            attributes.push({key: 'minCropLevelThreshold', value: vm.silo.minCropLevelThreshold});
            attributes.push({key: 'disableAlarms', value: vm.silo.disableAlarms});
            attributes.push({key: 'viewrotation', value: vm.silo.viewrotation});
            attributes.push({key: 'coordinate3dX', value: vm.silo.coordinate3dX});
            attributes.push({key: 'coordinate3dY', value: vm.silo.coordinate3dY});
            attributes.push({key: 'coordinate3dZ', value: vm.silo.coordinate3dZ});
            attributes.push({key: 'maxHeadSpaceTemperatureThreshold', value: vm.silo.maxHeadSpaceTemperatureThreshold});
            attributes.push({key: 'maxHeadSpaceHumidityThreshold', value: vm.silo.maxHeadSpaceHumidityThreshold});
            attributes.push({key: 'maxHeadSpaceCo2WarningThreshold', value: vm.silo.maxHeadSpaceCo2WarningThreshold});
            attributes.push({key: 'maxHeadSpaceCo2CriticalThreshold', value: vm.silo.maxHeadSpaceCo2CriticalThreshold});
            attributes.push({key: 'minHeadSpaceDewpointTemperatureDiffThreshold', value: vm.silo.minHeadSpaceDewpointTemperatureDiffThreshold});
            attributes.push({key: 'autoDisableSensors', value: vm.silo.autoDisableSensors});
            attributeService.saveEntityAttributes(vm.silo.id.entityType, vm.silo.id.id,
                types.attributesScope.server.value, attributes)
                .then(function success() {});
        }
    });

    $scope.$watch("vm.levelIndicator.cycle_time + vm.levelIndicator.blocked", (newVal, prevValue) => {
        if (vm.silo && vm.levelIndicator && !$scope.theForm.$invalid && angular.isDefined(prevValue)) {
            var attributes = [];
            attributes.push({key: agTypes.attributes.levelIndicatorShared.cycleTime, value: vm.levelIndicator.cycle_time});
            attributes.push({key: agTypes.attributes.levelIndicatorShared.blocked, value: vm.levelIndicator.blocked});
            attributeService.saveEntityAttributes(vm.levelIndicator.id.entityType, vm.levelIndicator.id.id, types.attributesScope.shared.value, attributes).then(
                () => {}
            );
        }
    });

    $scope.$watch('vm.levelIndicator.levelPositionX + vm.levelIndicator.levelPositionY + vm.levelIndicator.levelPositionZ', () => {
        if (vm.silo && vm.levelIndicator) {
            let measurePoints = [{
                id: 1,
                pos: {}
            }];
            measurePoints[0].pos.posX = vm.levelIndicator.levelPositionX;
            measurePoints[0].pos.posY = vm.levelIndicator.levelPositionY;
            measurePoints[0].pos.posZ = vm.levelIndicator.levelPositionZ;
            let attributes = [{key: agTypes.attributes.levelIndicatorServer.measurePoints, value: angular.toJson(measurePoints)}];
            if (angular.isDefined(vm.levelIndicator.levelPositionX) && angular.isDefined(vm.levelIndicator.levelPositionY) && angular.isDefined(vm.levelIndicator.levelPositionZ)) {
                attributeService.saveEntityAttributes(vm.levelIndicator.id.entityType, vm.levelIndicator.id.id, types.attributesScope.server.value, attributes).then(
                    () => {}
                )
            }
        }
    });

    agUtils.getCropNames().then(
        (response) => {
            vm.cropNames = [];
            var cropNames = response.jsonValue;
            for (var property in cropNames) {
                var cropNameObj = {};
                cropNameObj.nameId = property;
                cropNameObj.value = cropNames[property];
                vm.cropNames.push(cropNameObj);
            }
            vm.cropNames.sort((a,b) => a.value.placement - b.value.placement)            
        }
    );

    agUtils.getCropValues().then(
        (response) => {
            vm.cropValues = response.jsonValue;
        }
    );

    function changeCropType() {
        attributeService.saveEntityTimeseries(vm.silo.id.entityType, vm.silo.id.id, "scope", [{key: agTypes.telemetry.silo.cropType, value: vm.silo.cropType.nameId}]);

        // We reset the custom crop density settings on crop type change.
        var attributes = [];
        attributes.push({key: 'customCropDensity', value: null});
        attributes.push({key: 'customCropGlidingAngle', value: null});
        attributeService.saveEntityAttributes(vm.silo.id.entityType, vm.silo.id.id,
            types.attributesScope.server.value, attributes)
            .then(function success() {
                // We intentionally fetch all silo related data all over again because of custom overrides
                // like density settings etc. that depends on crop type in order to keep the whole ui up to date.
                loadSilo();
            });
    }

    function updateEnabledSensors(enabledSensors) {
        if (vm.silo) {
            var attributes = [];
            attributes.push({key: 'enabledSensors', value: angular.toJson(enabledSensors)});
            attributeService.saveEntityAttributes(vm.silo.id.entityType, vm.silo.id.id,
                types.attributesScope.server.value, attributes)
                .then(function success() {});
        }
    }

    function loadSilo() {
        vm.silo = null;
        reset();
        
        if (vm.siloId) {
            vm.loading = true;
            siloService.getSilo(vm.siloId).then(
                function success(silo) {
                    vm.silo = silo;
                    if (!vm.agTypes.siloType[vm.silo.siloType]) {
                        vm.silo.siloType = vm.agTypes.siloType.cylinder;
                    }
                    
                    var siloSub = [{
                        type: types.datasourceType.entity,
                        entityType: 'ASSET',
                        entityId: vm.siloId.id,
                        entityName: vm.silo.name,
                        timeseries: [],
                        attributes: []
                    }];
                    var enabledSensorsKey = {
                        name: "changed-enabledSensors",
                        label: "Enabled Sensors Changed"
                    };
                    vm.enabledSensorsSub = {
                        callbacks: {
                            onDataUpdated: (e) => {
                                if (!vm.silo.autoDisableSensors) return;
                                for (var value of e.data) {
                                    if (value.dataKey.name === enabledSensorsKey.name && value.data.length > 0) {
                                        vm.silo.enabledSensors = value.data[0][1];
                                        $scope.$digest();
                                        break;
                                    }
                                }
                            },
                            onDataUpdateError: () => {},
                            dataLoading: () => {}
                        },
                        useDashboardTimewindow: false
                    };

                    siloSub[0].timeseries.push(enabledSensorsKey);

                    vm.enabledSensorsSub.timeWindowConfig = angular.copy(vm.ctx.dashboardTimewindow);
                    vm.enabledSensorsSub.timeWindowConfig.aggregation.type = types.aggregation.none.value;
                    vm.enabledSensorsSub.timeWindowConfig.aggregation.limit = 1;
                    vm.ctx.subscriptionApi.createSubscriptionFromInfo(
                        types.widgetType.timeseries.value, siloSub, vm.enabledSensorsSub, false, true).then(
                        (subscription) => {
                            vm.siloSubscription = subscription;
                        }
                    );

                    reloadSiloSensorLinesAndCropTypeAndHeadSpaceSensor().then(
                        (cropResponse) => {
                            vm.silo.cropType = null;
                            var cropType = cropResponse.crop_type[0].value;
                            if (vm.cropNames) {
                                var currentCropName = $filter('filter')(vm.cropNames, {nameId: cropType}, true);
                                if (currentCropName && currentCropName.length) {
                                    vm.silo.cropType = currentCropName[0];

                                    if (vm.cropValues && vm.cropValues[cropType]) {
                                        vm.silo.defaultCropDensity = vm.cropValues[cropType].density;
                                        vm.silo.defaultGlidingAngle = vm.cropValues[cropType].gliding_angle;
                                    }
                                }
                            }
                            vm.loading = false;
                        },
                        () => {
                            vm.sensorLines = [];
                            vm.loading = false;
                        }
                    );
                },
                function fail() {
                    vm.loading = false;
                }
            );
        }
    }

    function reset() {
        if (vm.siloSubscription) {
            vm.ctx.subscriptionApi.removeSubscription(vm.siloSubscription.id);
        }
    }

    function reloadSiloSensorLinesAndCropTypeAndHeadSpaceSensor() {
        var deferred = $q.defer();
        if (vm.siloId) {
            siloService.getSiloSensorLines(vm.siloId).then(
                (sensorLines) => {
                    if (vm.silo) {
                        siloService.excludeUnconnectedSensorLines(sensorLines);
                        vm.sensorLines = $filter('orderBy')(sensorLines, agTypes.attributes.temperatureSensorLinesClient.lineNum);
                        vm.isMoistureLines = false;
                        var moistureSensorLines = $filter('filter')(vm.sensorLines, {type: agTypes.deviceType.moistureSensorLines});
                        if (moistureSensorLines.length) {
                            vm.isMoistureLines = true;
                        }

                        vm.isCO2Lines = false;
                        var co2SensorLines = $filter('filter')(vm.sensorLines, {type: agTypes.deviceType.co2SensorLines});
                        if (co2SensorLines.length) {
                            vm.isCO2Lines = true;
                        }
                    }
                    siloService.getSiloHeadSpaceSensor(vm.siloId).then(
                        (headSpaceSensor) => {
                            if (headSpaceSensor[0]) {
                                vm.headSpaceSensor = headSpaceSensor[0];
                            }
                            checkForLevelIndicator().then(
                                (cropResponse) => {
                                    deferred.resolve(cropResponse);
                                },
                                () => {
                                    deferred.reject();
                                }
                            );
                        },
                        () => {
                            checkForLevelIndicator().then(
                                (cropResponse) => {
                                    deferred.resolve(cropResponse);
                                },
                                () => {
                                    deferred.reject();
                                }
                            );
                        }
                    );
                },
                () => {
                    deferred.reject();
                }
            );
        }
        return deferred.promise;
    }

    function syncLevelIndicatorAttributes() {
        var deferred = $q.defer();
        var attributes = [];
        if (angular.isDefined(vm.levelIndicator.blocked) && vm.levelIndicator.blocked !== null) {
            attributes.push({key: agTypes.attributes.levelIndicatorShared.blocked, value: vm.levelIndicator.blocked});
        }
        if (angular.isDefined(vm.levelIndicator.cycle_time) && vm.levelIndicator.cycle_time !== null) {
            attributes.push({key: agTypes.attributes.levelIndicatorShared.cycleTime, value: vm.levelIndicator.cycle_time});
        }
        attributeService.saveEntityAttributes(vm.levelIndicator.id.entityType, vm.levelIndicator.id.id, types.attributesScope.shared.value, attributes).then(
            () => {
                deferred.resolve();
            },
            () => {
                deferred.reject();
            }
        );
        return deferred.promise;
    }

    function checkForLevelIndicator() {
        var deferred = $q.defer();
        attributeService.getEntityLatestTimeseriesValues(vm.silo.id.entityType, vm.silo.id.id, [agTypes.telemetry.silo.cropType]).then(
            (cropResponse) => {
                siloService.getSiloLevelIndicator(vm.siloId).then(
                    (levelIndicator) => {
                        if (levelIndicator[0] && levelIndicator[0].mode != 'auto') {                            
                            vm.levelIndicator = levelIndicator[0];
                            let measurePoints = angular.fromJson(vm.levelIndicator.measurePoints);
                            vm.levelIndicator.levelPositionX = measurePoints[0].pos.posX;
                            vm.levelIndicator.levelPositionY = measurePoints[0].pos.posY;
                            vm.levelIndicator.levelPositionZ = measurePoints[0].pos.posZ;
                            syncLevelIndicatorAttributes().then(
                                () => {
                                    deferred.resolve(cropResponse);
                                },
                                () => {
                                    deferred.resolve(cropResponse);
                                }
                            );
                        } else {
                            deferred.resolve(cropResponse);
                        }
                    },
                    () => {
                        deferred.reject();
                    }
                )
            },
            () => {
                deferred.reject();
            }
        );
        return deferred.promise;
    }

    function show3DSettings() {
        const siteParams = vm.ctx.stateController.getStateParamsByStateId(agTypes.clientState.site);

        if (userService.getAuthority() === 'CUSTOMER_USER') {
            return siteParams?.enable3D ?? false;
        }
        return true;
    }
}